import React, { useState, useContext, useEffect } from 'react';
import { OrganizationJobsContext } from '../../contexts/OrganizationJobsContext';
import { UserContext } from '../../contexts/UserContext';
import { Plus, Trash2, ArrowLeft, RefreshCw } from 'lucide-react';
import { useOrganizations } from '../../contexts/OrganizationsContext';
import { daysOfWeek } from '../../types/constants';
import { JobSchedule } from '../../types/job';

export default function JobEditorPage({
  editItem,
  onBack,
  onSuccess
}: {
  editItem?: any;
  onBack: () => void;
  onSuccess: () => void;
}) {
  const { createOrganizationJob, updateOrganizationJob, organizationJobs } =
    useContext(OrganizationJobsContext);
  const { userProfile } = useContext(UserContext);
  const { organizations } = useOrganizations();

  const [formState, setFormState] = useState({
    jobName: editItem?.jobName || '',
    jobDescription: editItem?.jobDescription || '',
    jobPoints: editItem?.jobPoints || 0,
    payEvery: editItem?.payEvery || 0,
    payDayOfWeek:
      typeof editItem?.payDayOfWeek === 'number' ? editItem.payDayOfWeek : 0,
    firstPayDate: editItem?.firstPayDate
      ? editItem.firstPayDate.split('T')[0]
      : '',
    organizationId: editItem?.organizationId || '',
    maxPositions: editItem?.maxPositions || 0,
    skills: editItem?.skills ? editItem.skills.join(', ') : '',
    requirements: editItem?.requirements
      ? editItem.requirements.join(', ')
      : '',
    icon: editItem?.icon || '',
    jobLocation: editItem?.jobLocation || '',
    jobSlug: editItem?.jobSlug || ''
  });

  const [schedules, setSchedules] = useState<JobSchedule[]>([]);

  const newEmptySchedule = (): JobSchedule => ({
    dayOfWeek: 1,
    startTime: '09:00',
    endTime: '10:00'
  });

  // Generate a slug
  const generateSlug = (name: string) => {
    return name
      .toLowerCase()
      .replace(/[^\w\s-]/g, '') // remove non-word
      .replace(/\s+/g, '-') // replace spaces with hyphens
      .replace(/-+/g, '-'); // remove multiple hyphens
  };

  // If jobSlug is empty, create it from jobName
  useEffect(() => {
    if (!formState.jobSlug) {
      setFormState((prev) => ({
        ...prev,
        jobSlug: generateSlug(formState.jobName)
      }));
    }
  }, [formState.jobName, formState.jobSlug]);

  // If editing an existing job, we can fetch fresh data from context
  useEffect(() => {
    if (editItem && organizationJobs && organizationJobs.length > 0) {
      const jobId = Number(editItem.organizationJobId);
      const freshJob = organizationJobs.find(
        (item: any) => Number(item.organizationJobId) === jobId
      );
      if (freshJob) {
        setFormState({
          jobName: freshJob.jobName || '',
          jobDescription: freshJob.jobDescription || '',
          jobPoints: freshJob.jobPoints || 0,
          payEvery: freshJob.payEvery || 0,
          payDayOfWeek:
            typeof freshJob.payDayOfWeek === 'number' ? freshJob.payDayOfWeek : 0,
          firstPayDate: freshJob.firstPayDate
            ? freshJob.firstPayDate.split('T')[0]
            : '',
          organizationId: freshJob.organizationId,
          maxPositions: freshJob.maxPositions || 0,
          skills: Array.isArray(freshJob.skills)
            ? freshJob.skills.join(', ')
            : '',
          requirements: Array.isArray(freshJob.requirements)
            ? freshJob.requirements.join(', ')
            : '',
          icon: freshJob.icon || '',
          jobLocation: freshJob.jobLocation || '',
          jobSlug: freshJob.jobSlug || generateSlug(freshJob.jobName || '')
        });

        // If the job has schedules, convert HH:MM:SS -> HH:MM
        if (freshJob.organizationJobSchedules && Array.isArray(freshJob.organizationJobSchedules)) {
          const formattedSchedules = freshJob.organizationJobSchedules.map(
            (sch: any) => ({
              ...sch,
              startTime: sch.startTime.substring(0, 5),
              endTime: sch.endTime.substring(0, 5)
            })
          );
          setSchedules(formattedSchedules);
        } else {
          setSchedules([newEmptySchedule()]);
        }
      }
    } else {
      // brand new job
      setSchedules([newEmptySchedule()]);
    }
  }, [editItem, organizationJobs]);

  // If creating a new job, default to the first organization if not set
  useEffect(() => {
    if (!editItem && organizations && organizations.length > 0 && !formState.organizationId) {
      setFormState((prev) => ({
        ...prev,
        organizationId: organizations[0].organizationId
      }));
    }
  }, [organizations, editItem, formState.organizationId]);

  const handleAddSchedule = () => {
    setSchedules((prev) => [...prev, newEmptySchedule()]);
  };

  const handleRemoveSchedule = (index: number) => {
    setSchedules((prev) => {
      const updated = [...prev];
      updated.splice(index, 1);
      return updated;
    });
  };

  const handleScheduleChange = (
    index: number,
    field: keyof JobSchedule,
    value: any
  ) => {
    setSchedules((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // enforce firstPayDate
    if (!formState.firstPayDate) {
      alert('First Pay Date is required.');
      return;
    }
    // Ensure schedule times have :00 seconds for the backend
    const formattedSchedules = schedules.map((sch) => ({
      ...sch,
      startTime:
        sch.startTime.length === 5 ? `${sch.startTime}:00` : sch.startTime,
      endTime:
        sch.endTime.length === 5 ? `${sch.endTime}:00` : sch.endTime
    }));

    const payload = {
      organizationJobId: editItem?.organizationJobId || 0,
      organizationId: formState.organizationId,
      jobName: formState.jobName,
      jobSlug: formState.jobSlug || generateSlug(formState.jobName),
      jobDescription: formState.jobDescription,
      jobPoints: formState.jobPoints,
      payEvery: formState.payEvery,
      payDayOfWeek: formState.payDayOfWeek,
      firstPayDate: new Date(formState.firstPayDate).toISOString(),
      maxPositions: formState.maxPositions,
      skills: formState.skills
        ? formState.skills
            .split(',')
            .map((s: string) => s.trim())
            .filter(Boolean)
        : [],
      requirements: formState.requirements
        ? formState.requirements
            .split(',')
            .map((s: string) => s.trim())
            .filter(Boolean)
        : [],
      icon: formState.icon,
      jobLocation: formState.jobLocation,
      organizationJobAssignments: editItem?.organizationJobAssignments || [],
      organizationJobSchedules: formattedSchedules,
      currentPositions: editItem?.currentPositions || 0,
      createdAt: editItem?.createdAt || new Date().toISOString(),
      createdBy: editItem?.createdBy || userProfile?.userId,
      updatedAt: new Date().toISOString(),
      updatedBy: userProfile?.userId,
      deactivatedOn: editItem?.deactivatedOn || null
    };

    try {
      if (editItem) {
        await updateOrganizationJob(editItem.organizationJobId, payload);
      } else {
        await createOrganizationJob(payload);
      }
      onSuccess();
    } catch (error) {
      console.error('Error saving job:', error);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <button
            onClick={onBack}
            className="p-2 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <h2 className="text-xl font-semibold text-gray-800">
            {editItem ? 'Edit Job Position' : 'Create New Job Position'}
          </h2>
        </div>
      </div>

      <div className="dashboard-card p-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Organization dropdown (only for new jobs) */}
          {!editItem && organizations && organizations.length > 0 && (
            <div className="mb-6 border-b border-gray-200 pb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Organization
              </label>
              <select
                value={formState.organizationId}
                onChange={(e) =>
                  setFormState({ ...formState, organizationId: e.target.value })
                }
                className="w-full px-3 py-2 bg-white/50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors"
                required
              >
                <option value="">Select an organization</option>
                {organizations.map((org: any) => (
                  <option key={org.organizationId} value={org.organizationId}>
                    {org.organizationName}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* 1. Job Details */}
            <div className="space-y-4">
              <h3 className="font-medium text-gray-700 text-lg">Job Details</h3>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Job Name
                </label>
                <input
                  type="text"
                  value={formState.jobName}
                  onChange={(e) =>
                    setFormState({ ...formState, jobName: e.target.value })
                  }
                  className="w-full px-3 py-2 bg-white/50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Job Slug (URL-friendly identifier)
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    title="URL-friendly identifier for the job."
                    value={formState.jobSlug}
                    onChange={(e) =>
                      setFormState({ ...formState, jobSlug: e.target.value })
                    }
                    className="w-full px-3 py-2 bg-white/50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors"
                  />
                  <button
                    type="button"
                    onClick={() =>
                      setFormState({
                        ...formState,
                        jobSlug: generateSlug(formState.jobName)
                      })
                    }
                    className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                    title="Generate slug from job name"
                  >
                    <RefreshCw className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>

            {/* 2. Position Details */}
            <div className="space-y-4">
              <h3 className="font-medium text-gray-700 text-lg">
                Position Details
              </h3>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Points per Shift
                </label>
                <input
                  type="number"
                  value={formState.jobPoints}
                  onChange={(e) =>
                    setFormState({ ...formState, jobPoints: +e.target.value })
                  }
                  className="w-full px-3 py-2 bg-white/50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Max Positions
                </label>
                <input
                  type="number"
                  value={formState.maxPositions}
                  onChange={(e) =>
                    setFormState({ ...formState, maxPositions: +e.target.value })
                  }
                  className="w-full px-3 py-2 bg-white/50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors"
                />
              </div>
            </div>

            {/* 3. Payment Details */}
            <div className="space-y-4">
              <h3 className="font-medium text-gray-700 text-lg">
                Payment Details
              </h3>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Pay Every (weeks)
                </label>
                <input
                  type="number"
                  value={formState.payEvery}
                  onChange={(e) =>
                    setFormState({ ...formState, payEvery: +e.target.value })
                  }
                  className="w-full px-3 py-2 bg-white/50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Pay Day of Week
                </label>
                <select
                  value={formState.payDayOfWeek}
                  onChange={(e) =>
                    setFormState({
                      ...formState,
                      payDayOfWeek: Number(e.target.value)
                    })
                  }
                  className="w-full px-3 py-2 bg-white/50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors"
                >
                  {daysOfWeek.map((day, index) => (
                    <option key={index} value={index}>
                      {day}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* location & firstPayDate in a row */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Job Location
              </label>
              <input
                type="text"
                value={formState.jobLocation}
                onChange={(e) =>
                  setFormState({ ...formState, jobLocation: e.target.value })
                }
                className="w-full px-3 py-2 bg-white/50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                First Pay Date
              </label>
              <input
                type="date"
                value={formState.firstPayDate}
                onChange={(e) =>
                  setFormState({ ...formState, firstPayDate: e.target.value })
                }
                required
                className="w-full px-3 py-2 bg-white/50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Skills (comma separated)
              </label>
              <input
                type="text"
                value={formState.skills}
                onChange={(e) =>
                  setFormState({ ...formState, skills: e.target.value })
                }
                className="w-full px-3 py-2 bg-white/50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors"
              />
            </div>
            <div className="space-y-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Requirements (comma separated)
              </label>
              <input
                type="text"
                value={formState.requirements}
                onChange={(e) =>
                  setFormState({ ...formState, requirements: e.target.value })
                }
                className="w-full px-3 py-2 bg-white/50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors"
              />
            </div>
          </div>

          <div className="mt-6">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Job Description
            </label>
            <textarea
              rows={4}
              value={formState.jobDescription}
              onChange={(e) =>
                setFormState({ ...formState, jobDescription: e.target.value })
              }
              className="w-full px-3 py-2 bg-white/50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors"
            />
          </div>

          {/* Schedules */}
          <div>
            <h3 className="font-medium text-gray-700 text-lg mb-3">
              Job Schedules
            </h3>
            <div className="bg-gray-50/70 p-4 rounded-lg">
              {schedules.map((schedule, index) => (
                <div
                  key={index}
                  className="flex flex-col mb-4 pb-4 border-b border-gray-200 last:border-b-0 last:mb-0 last:pb-0"
                >
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div className="w-full md:w-auto">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Day of Week
                      </label>
                      <div className="flex flex-wrap gap-2">
                        {daysOfWeek.map((day, i) => (
                          <label
                            key={i}
                            className={`px-3 py-2 rounded-lg border text-sm font-medium cursor-pointer transition-colors ${
                              schedule.dayOfWeek === i
                                ? 'bg-indigo-100 border-indigo-300 text-indigo-800'
                                : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
                            }`}
                          >
                            <input
                              type="radio"
                              name={`day-of-week-${index}`}
                              value={i}
                              checked={schedule.dayOfWeek === i}
                              onChange={() =>
                                handleScheduleChange(index, 'dayOfWeek', i)
                              }
                              className="sr-only"
                            />
                            {day.substring(0, 3)}
                          </label>
                        ))}
                      </div>
                    </div>

                    <div className="flex flex-col md:flex-row items-start md:items-end gap-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Start Time
                        </label>
                        <input
                          type="time"
                          value={schedule.startTime}
                          onChange={(e) =>
                            handleScheduleChange(index, 'startTime', e.target.value)
                          }
                          className="px-3 py-2 bg-white border border-gray-200 rounded-lg w-full md:w-32"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          End Time
                        </label>
                        <input
                          type="time"
                          value={schedule.endTime}
                          onChange={(e) =>
                            handleScheduleChange(index, 'endTime', e.target.value)
                          }
                          className="px-3 py-2 bg-white border border-gray-200 rounded-lg w-full md:w-32"
                        />
                      </div>

                      {schedules.length > 1 && (
                        <button
                          type="button"
                          onClick={() => handleRemoveSchedule(index)}
                          className="bg-red-50 text-red-500 p-2 rounded-lg hover:bg-red-100 transition-colors self-end"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
              <button
                type="button"
                onClick={handleAddSchedule}
                className="mt-3 flex items-center justify-center gap-1 px-4 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors w-full"
              >
                <Plus className="w-4 h-4" />
                Add Another Schedule
              </button>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onBack}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600 transition-colors font-medium"
            >
              {editItem ? 'Update Job' : 'Create Job'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
